import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FormikProvider, useFormik } from 'formik'
import currency from 'currency.js'
import { faPercent, faChevronRight } from '@fortawesome/pro-regular-svg-icons'
import { useAnalyticsTrack } from '@/features/amplitude'
import {
  Button,
  Card,
  CardColors,
  Dialog,
  Form,
  Image,
  LinkCard,
  Separator,
  Tab,
  Text,
} from '@/ui'
import {
  centsToDollars,
  dollarsToCents,
  formatCurrency,
} from '@/utils/currencyHelpers'
import { IncomeTab } from '@/features/goal-setting/IncomeTab'
import { useAppDispatch } from '@/utils/typeHelpers'
import { ExpensesTab } from '@/features/goal-setting/ExpensesTab'
import { ProfitTab } from '@/features/goal-setting/ProfitTab'
import { updateFinancialProfile } from '@/actions/financialProfileActions'
import { useReselector } from '@/utils/sharedHooks'
import { getFinancialProfile } from '@/selectors/user.selectors'
import { GOAL_SETTING_SCREENS } from '@/features/goal-setting'
import { IncomeCalculator } from '@/features/goal-setting/IncomeCalculator'
import { fetchUserTransactions } from '@/features/transactions/transactions.slice'
import { getProfitGoal } from '@/features/goal-setting/helpers'
import { getFieldNames, makeNumberSchema } from '@/base'
import { ImageBackgroundColors, TypographyStyles } from '@/styles'

export const SettingFinancialGoals = ({
  goBack,
}: {
  goBack: (newScreen: string | null, replace?: boolean | undefined) => void
}) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const fp = useReselector(getFinancialProfile)
  const track = useAnalyticsTrack()

  useEffect(() => {
    dispatch(fetchUserTransactions())
  }, [dispatch])

  const formik = useFormik({
    initialValues: {
      incomeGoal: fp?.annualIncomeGoalInCents
        ? centsToDollars(fp?.annualIncomeGoalInCents)
        : '',
      expenseRatio: fp?.annualExpenseRatioGoal
        ? fp?.annualExpenseRatioGoal * 100
        : '30',
    },
    onSubmit: async ({ incomeGoal, expenseRatio }) => {
      const isInitialGoal = fp?.annualIncomeGoalInCents === null

      await dispatch(
        updateFinancialProfile(fp?.id, {
          annualIncomeGoalInCents: dollarsToCents(incomeGoal),
          annualExpenseRatioGoal: Number(expenseRatio) / 100,
        })
      )

      track(
        isInitialGoal
          ? 'Saved Initial Practice Profit Goals'
          : 'Updated Practice Profit Goals'
      )

      setOpen(true)
      track('Viewed Practice Profit Goals Success Modal')
    },
  })

  const {
    values: { expenseRatio, incomeGoal },
    submitForm,
    setFieldValue,
  } = formik

  const fieldNames = getFieldNames(formik)

  const profitGoal = useMemo(
    () => getProfitGoal(dollarsToCents(incomeGoal), Number(expenseRatio) / 100),
    [expenseRatio, incomeGoal]
  )

  const monthlyIncome = useMemo(
    () =>
      isNaN(Number(incomeGoal)) ? 0 : currency(incomeGoal).divide(12).value,
    [incomeGoal]
  )

  const monthlyExpense = useMemo(() => {
    if (isNaN(Number(incomeGoal)) || isNaN(Number(expenseRatio))) {
      return 0
    }
    return currency(incomeGoal).multiply(expenseRatio).divide(100).divide(12)
      .value
  }, [expenseRatio, incomeGoal])

  const monthlyProfit = useMemo(
    () => (profitGoal === 0 ? 0 : currency(profitGoal).divide(12).value),
    [profitGoal]
  )

  const panes = useMemo(
    () => [
      { menuItem: 'Income', render: () => <IncomeTab /> },
      { menuItem: 'Expenses', render: () => <ExpensesTab /> },
      { menuItem: 'Profit', render: () => <ProfitTab /> },
    ],
    []
  )

  const handleTabClick = (index: number) => {
    const tabNames = panes.map((pane) => pane.menuItem)
    track(`Clicked goal setting ${tabNames[index]} tab`)
  }

  return (
    <FormikProvider value={formik}>
      <div className="mt-5 flex flex-col items-center mx-auto w-5/8 md:w-7/8 lg:w-7/8">
        <Text variant={TypographyStyles.DISPLAY_MD} className="w-full">
          Setting Financial Goals
        </Text>
        <Text variant={TypographyStyles.BODY_SM} className="w-full mt-4">
          Data benchmarks have been provided for your context. Adjust income and
          expense targets to see how they affect profit.
        </Text>
        <Card className="mt-10 w-full">
          <div className="flex gap-4">
            <div className="w-1/2">
              <Form.Input
                label="Annual Income Goal"
                name={fieldNames.incomeGoal}
                schema={makeNumberSchema({
                  minNumber: 0,
                  allowedDecimals: 2,
                })}
                componentType="currency"
                onBlur={() => track('Updated annual income goal input')}
                message={
                  monthlyIncome
                    ? `Average of ${formatCurrency(monthlyIncome, { hideDecimalsIfZero: true })}/mo`
                    : undefined
                }
              />
            </div>
            <div className="w-1/2">
              <Form.Input
                label="Expense Ratio Goal"
                name={fieldNames.expenseRatio}
                type="number"
                schema={makeNumberSchema({ maxNumber: 100, minNumber: 0 })}
                iconRight={faPercent}
                onBlur={() => track('Updated expense ratio goal input')}
                message={
                  monthlyIncome
                    ? `Average of ${formatCurrency(monthlyExpense, { hideDecimalsIfZero: true })}/mo`
                    : undefined
                }
              />
            </div>
          </div>
          <Card color={CardColors.PRIORITY}>
            <div className="flex w-full">
              <div className="w-3/4">
                <Text variant={TypographyStyles.LABEL_MD}>
                  Annual Profit Goal
                </Text>
                <br />
                <Text variant={TypographyStyles.DISPLAY_MD}>
                  {formatCurrency(profitGoal, {
                    hideDecimalsIfZero: true,
                  })}
                </Text>
                <br />
                {monthlyProfit ? (
                  <Text variant={TypographyStyles.BODY_XS}>
                    Average of{' '}
                    {formatCurrency(monthlyProfit, {
                      hideDecimalsIfZero: true,
                    })}
                    /mo
                  </Text>
                ) : (
                  // this adds spacing equal to above so content does not jump
                  <Text variant={TypographyStyles.BODY_XS}>&nbsp;</Text>
                )}
              </div>
              <div className="w-1/4">
                <Image
                  src="https://heard-images.s3.amazonaws.com/assets/coins-stack-sparkle.svg"
                  alt="coin-stack-sparkle"
                  height={80}
                />
              </div>
            </div>
          </Card>
          <Button onClick={submitForm} className="w-full">
            Save Goals
          </Button>
        </Card>

        <Tab
          panes={panes}
          onTabClick={handleTabClick}
          variant="outline"
          fullWidth
          className="mt-10 w-full"
        />

        <IncomeCalculator
          currentGoal={incomeGoal ? Number(incomeGoal) : null}
          updateIncomeGoal={(val) => setFieldValue(fieldNames.incomeGoal, val)}
        />

        <Text
          variant={TypographyStyles.H1}
          textAlign="left"
          className="w-full mt-8"
        >
          Learn More
        </Text>
        <Text
          variant={TypographyStyles.BODY_MD}
          textAlign="left"
          className="w-full mt-2"
        >
          Need more guidance? Check out these resources for more help.
        </Text>

        <div className="flex gap-4 w-full mt-4">
          <LinkCard
            imageAlt="financials"
            imageSrc="https://heard-images.s3.amazonaws.com/assets/financials.svg"
            title="Report"
            description="State of Private Practice Report"
            className="w-1/2"
            imageColor={ImageBackgroundColors.YELLOW}
          >
            <Button
              variant="secondary"
              onClick={() => {
                track('Clicked "state of private practice report" resource')
                window.open(
                  'https://7507639.fs1.hubspotusercontent-na1.net/hubfs/7507639/Heard%202025%20Financial%20State%20of%20Private%20Practice%20Report.pdf',
                  '_blank'
                )
              }}
            >
              View
            </Button>
          </LinkCard>
          <LinkCard
            imageAlt="calculator"
            imageSrc="https://heard-images.s3.amazonaws.com/assets/calculator3.svg"
            title="Tools"
            description="Heard’s Session Fee Setting Calculator"
            className="w-1/2"
            imageColor={ImageBackgroundColors.YELLOW}
          >
            <Button
              variant="secondary"
              onClick={() => {
                track('Clicked "tracking therapy practice expenses" resource')
                window.open(
                  'https://www.joinheard.com/articles/how-to-track-expenses-for-your-therapy-practice',
                  '_blank'
                )
              }}
            >
              View
            </Button>
          </LinkCard>
        </div>

        <Separator className="mt-10" />
        <div className="w-full flex justify-between mt-6">
          <Button
            onClick={() => goBack(GOAL_SETTING_SCREENS.intro)}
            variant="secondary"
          >
            Back
          </Button>
          <Button onClick={submitForm} iconRight={faChevronRight}>
            Save Goals
          </Button>
        </div>
      </div>
      <Dialog open={open} onClose={() => navigate('/')}>
        <Dialog.Content>
          <Dialog.Title>Goals Saved</Dialog.Title>
          <Card color={CardColors.SUCCESS} className="flex justify-center">
            <Image
              src="https://heard-images.s3.amazonaws.com/assets/check.svg"
              alt="check"
              height={80}
              className="my-4"
            />
          </Card>
          <Text>
            We’ve saved your goals. You’ll now be able to track the progress of
            your income, expense, and profit targets. We recommend you
            periodically refresh your goals as you progress throughout the year.
          </Text>

          <Dialog.Footer>
            <Dialog.Close asChild>
              <Button onClick={() => navigate('/')}>Done</Button>
            </Dialog.Close>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog>
    </FormikProvider>
  )
}
